import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@mui/material';
import { TFormType } from 'src/@types';
import { FormType } from 'src/static/constants/common/form';

import DialogTitleWithCloseButton from './DialogTitleWithCloseButton';

// 다이얼로그를 열기 위한 매개변수 인터페이스
interface OpenDialogParams {
  id: string;
  formType?: TFormType; // form 타입: 'CREATE' | 'UPDATE' | 'READONLY
  dialogContent: ReactNode; // form 컨텐츠
  setOnClose?: () => void;
  title?: string;
  subTitle?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}
// multi 사용을 위한 다이얼로그 상태 인터베이스
interface DialogState {
  id: string;
  formType?: TFormType;
  dialogContent: ReactNode;
  onClose: () => void;
  title?: string;
  subTitle?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}
// 다이얼로그 컨텍스트 인터페이스
interface DialogContextProps {
  formId: string;
  dialogs: DialogState[];
  openDialog: (params: OpenDialogParams) => void;
  closeDialog: (id: string) => void;
  closeAllDialogs: () => void;
}

// 초기값
const initialState: DialogContextProps = {
  formId: '',
  dialogs: [],
  openDialog: () => undefined,
  closeDialog: () => undefined,
  closeAllDialogs: () => undefined,
};

export const DialogContext = createContext<DialogContextProps>(initialState);
export const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialogContext must be use inside DialogProvider');
  return context;
};

type DialogProviderProps = {
  children: ReactNode;
};

export function DialogProvider({ children }: DialogProviderProps) {
  const { t } = useTranslation();
  const [dialogs, setDialogs] = useState<DialogState[]>([]);
  // 헤더 더블클릭 시 전체 화면 모드로 전환하기 위한 상태값
  const [fullScreenDialogs, setFullScreenDialogs] = useState<{ [key: string]: boolean }>({});
  // formId
  const [formId, setFormId] = useState<string>('');

  // 다이얼로그 열기
  const openDialog = ({
    id,
    formType,
    dialogContent,
    setOnClose: customOnClose,
    title,
    subTitle,
    maxWidth = 'md',
  }: OpenDialogParams) => {
    const newDialog = {
      id,
      formType,
      dialogContent,
      maxWidth,
      title,
      subTitle,
      onClose: () => {
        setDialogs((prevDialogs) => prevDialogs.filter((dialog) => dialog.id !== id));
        if (customOnClose) customOnClose();
      },
    };
    setDialogs((prevDialogs) => [...prevDialogs, newDialog]);
    setFormId(id);
  };

  // 다이얼로그 닫기
  const closeDialog = (id: string) => {
    setDialogs((prevDialogs) => prevDialogs.filter((dialog) => dialog.id !== id));
  };

  // 모든 다이얼로그 닫기
  const closeAllDialogs = () => {
    setDialogs([]);
  };

  // 헤더 더블클릭 시 전체 화면 모드로 전환
  const toggleFullScreen = (id: string) => {
    setFullScreenDialogs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // 타이틀 설정 함수
  const getTitle = (dialog: DialogState) => {
    if (dialog.title) {
      return dialog.title;
    }
    switch (dialog.formType) {
      case FormType.CREATE:
        return t('common.button.register');
      case FormType.UPDATE:
        return t('common.button.edit');
      case FormType.READONLY:
        return t('common.button.detail');
      default:
        return '';
    }
  };

  // hook으로 사용할 수 있도록 memoizedValue로 저장
  const memoizedValue = useMemo(
    () => ({
      dialogs,
      openDialog,
      closeDialog,
      closeAllDialogs,
      formId,
    }),
    [dialogs],
  );

  return (
    <DialogContext.Provider value={memoizedValue}>
      {children}
      {dialogs.map((dialog) => (
        <Dialog
          key={dialog.id}
          open
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            dialog.onClose();
          }}
          fullScreen={!!fullScreenDialogs[dialog.id]}
          fullWidth
          scroll="paper"
          maxWidth={dialog.maxWidth}
          sx={{ zIndex: 1300, backdropFilter: 'blur(1px)' }}
          disableEnforceFocus // ck-editor popup focus bugfix
        >
          <DialogTitleWithCloseButton
            title={getTitle(dialog)}
            subtitle={dialog.subTitle!}
            onClose={dialog.onClose}
            onDoubleClick={() => toggleFullScreen(dialog.id)}
          />
          <DialogContent dividers>{dialog.dialogContent}</DialogContent>
        </Dialog>
      ))}
    </DialogContext.Provider>
  );
}
